import type { ModuleFull } from "./setAsideTypes";
import type { FormikHelpers } from "formik";
import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import GenericTierTwo from "../GenericTierTwo";
import GenericModule from "../GenericModule";
import GenericInputGroup from "@components/input/inputGroup/GenericInputGroup";

import {
	initialValues,
	validationSchema,
	getSchemas,
	t2InitialValues,
} from "./setAside";
import {
	useAppSelector,
	useThreadsProcessor,
} from "@app/hooks";
import { BEModulesClassNames } from "@utils/moduleList";
import { selectCurrentProject } from "@features/project/projectSlice";
import {
	useContextAwareGetSetAsideDefaults,
	useContextAwareGetSetAside,
	useUpdateSetAsideMutation,
} from "@hooks/index";
import useErrorMessage from "@utils/useErrorMessage";
import useModuleHook from "../useModuleHook";
import useSpecialLandTypeInput from "../../hooks/useSpecialLandTypeInput";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import { FEModules } from "@utils/moduleList";

const SetAside = () => {
	const { activeActivityId } = useAppSelector(selectCurrentProject);
	const { moduleList } = useAppSelector(selectCurrentBuilder);
	const id = moduleList.find(({ id }) => id === FEModules.SetAside)?.uniqueId;

	const {
		data: setAsideData,
		isLoading,
		isError,
		error,
	} = useContextAwareGetSetAside(id ?? 0, { refetchOnMountOrArgChange: true });
	const { input, lucData, isLoadingLuc } = useSpecialLandTypeInput();

	const [
		updateSetAside,
		{ error: updateError, isError: isUpdateError, isLoading: isLoadingUpdate },
	] = useUpdateSetAsideMutation();

	const { data: defaults, error: defaultsError, isError: isDefaultsError, isLoading: isLoadingDefaults } = useContextAwareGetSetAsideDefaults(id ?? 0, { skip: !id });
	const { errorMsg: defaultsErrorMsg } = useErrorMessage({ isError: isDefaultsError, error: defaultsError });


	const { moduleSchema, t2Schema } = useMemo(() => getSchemas(), []);
	const [initValues, setInitValues] = useState<ModuleFull>({
		module: initialValues,
		tiertwo: t2InitialValues,
	});
	const { processModuleThreads } = useThreadsProcessor<ModuleFull>();
	const { errorMsg } = useModuleHook({
		skeletonsLoadingDeps: [isLoading, isLoadingDefaults, isLoadingLuc],
		isMutateError: isUpdateError,
		mutateError: updateError,
	});
	const { errorMsg: blockingError } = useErrorMessage({ isError, error });

	useEffect(() => {
		if (setAsideData) {
			setInitValues({
				module: setAsideData.module,
				tiertwo: { ...setAsideData.tiertwo, ...defaults },
				luc: lucData ? lucData.module : undefined,
			});
			processModuleThreads({
				module: setAsideData.module,
			})
		}
	}, [setAsideData, lucData, defaults, processModuleThreads]);

	const handleSubmit = async (
		values: ModuleFull,
		{ resetForm }: FormikHelpers<ModuleFull>,
	) => {
		const data: ModuleFull = { module: { ...values.module }, tiertwo: { ...values.tiertwo } } //we just need to ignore luc
		try {
			await updateSetAside({
				data,
				activityId: activeActivityId ?? 0,
			});
			resetForm({ values });
		} catch (error) {
			console.error(error);
		}
	};


	return (
		<Formik
			initialValues={initValues}
			onSubmit={handleSubmit}
			validationSchema={validationSchema}
			enableReinitialize
		>
			<>
				<GenericModule
					title="project.set_aside"
					t2Schema={t2Schema}
					beModuleType={BEModulesClassNames.SetAside}
					isLoading={isLoadingUpdate}
					footerError={errorMsg}
					blockingError={blockingError}
					moduleSchema={moduleSchema}
					isError={isUpdateError}
				>
					{() => (
						<div className="py-2">
							<GenericInputGroup inputGroup={input} />
						</div>
					)}
				</GenericModule>
				<GenericTierTwo
					title="project.set_aside"
					t2Schema={t2Schema}
					warningMessage={defaultsErrorMsg || null}
				/>
			</>
		</Formik>
	);
};


export default SetAside;
